import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { emitAdminLoginPing } from "../../state/AdminSession";

const AdminLogin = () => {
  const nav = useNavigate();

  // Never block the form on env/identity calls
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    reset,
  } = useForm({
    // critical: defaultValues so inputs render immediately
    defaultValues: { email: "", password: "" },
    mode: "onSubmit",
  });

  // safe focus (post-mount only, avoids hydration glitches)
  useEffect(() => {
    const t = setTimeout(() => setFocus("email"), 0);
    return () => clearTimeout(t);
  }, [setFocus]);

  const onSubmit = async (vals) => {
    setSubmitting(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/auth/login`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(vals),
        },
      );
      if (!r.ok) {
        const payload = await r.json().catch(() => ({}));
        throw new Error(payload?.detail || payload?.message || "Login failed");
      }

      const payload = await r.json().catch(() => ({}));

      // Check if MFA is required
      if (payload.mfa_required || payload.need_mfa) {
        reset();
        nav("/admin/mfa/verify", { replace: true });
        return;
      }

      // Login successful
      emitAdminLoginPing(); // Notify all components of successful login
      reset();
      nav("/admin/leads", { replace: true });
    } catch (e) {
      // show inline error (keep it simple + visible)
      alert(e.message ?? "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-3xl font-bold text-orange-500 text-center">
          BuildBright
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Admin Sign In
        </h2>
        <p className="mt-2 text-center text-sm text-zinc-300">
          Administrator access required
        </p>
      </div>

      <div
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        data-testid="admin-login-ready"
      >
        <div className="bg-zinc-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-6">
              <div>
                <label
                  className="block text-sm font-medium text-zinc-200"
                  htmlFor="admin-email"
                >
                  Email
                </label>
                <input
                  id="admin-email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  aria-invalid={!!errors.email}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-zinc-600 rounded-md bg-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  {...register("email", { required: "Email is required" })}
                  data-testid="admin-email"
                />
                {errors.email && (
                  <div role="alert" className="text-red-600 text-sm mt-1">
                    {errors.email.message}
                  </div>
                )}
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-zinc-200"
                  htmlFor="admin-password"
                >
                  Password
                </label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-zinc-600 rounded-md bg-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  {...register("password", {
                    required: "Password is required",
                  })}
                  data-testid="admin-password"
                />
                {errors.password && (
                  <div role="alert" className="text-red-600 text-sm mt-1">
                    {errors.password.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-submit"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
